import { useCallback } from 'react';

import { useCustomer } from '@order/Customer';

import type { AddressForm } from '../../AddressForm.types';

/**
 * Verifies if a proposed address already exists for that customer.
 * - checkIfDuplicateAddressByStreet
 * - checkIfDuplicateAddressByName
 */

export const useDuplicateCustomerAddressValidation = (addressId?: string) => {
  // Customer Addresses.
  const { customer } = useCustomer();
  const addresses = customer?.addresses;

  const checkIfDuplicateAddressByStreet = useCallback(
    (form?: AddressForm) => {
      return addresses?.some((address) => {
        const hasSameId = address.id === addressId;
        const hasSamePlaceId = address.googlePlaceId === form?.googlePlaceId;
        const hasSameStreet = address.street === form?.street;
        const hasSame2ndStreet =
          address.secondaryStreet === form?.secondaryStreet;
        const hasSameZipCode = address.zipCode === form?.zipCode;

        // Same Id means it's editing the current address, not duplicating it.
        if (hasSameId) return false;

        // Same Place Id means it's the same place (and a duplicate).
        if (hasSamePlaceId) return true;

        // Fallback in case google assigns a different place id for the same address.
        if (hasSameStreet && hasSame2ndStreet && hasSameZipCode) return true;

        // Addresses are not related.
        return false;
      });
    },
    [addresses, addressId],
  );

  const checkIfDuplicateAddressByName = useCallback(
    (addressName?: string) => {
      return addresses?.some((address) => {
        const hasSameId = address.id === addressId;
        const hasSameName =
          address.name?.toLowerCase() === addressName?.toLowerCase();

        // Same Id means it's editing the current address, not duplicating it.
        if (hasSameId) return false;

        // Duplicate by name.
        return hasSameName;
      });
    },
    [addresses, addressId],
  );

  return {
    checkIfDuplicateAddressByStreet,
    checkIfDuplicateAddressByName,
  };
};
